import React, { useState, useEffect } from 'react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '../Icons/Icons';
import './Hero.css';

//Background images
import heroBGImage1 from '../../assets/hero-bg-1.jpg';
import heroBGImage2 from '../../assets/hero-bg-2.jpg';
import heroBGImage3 from '../../assets/hero-bg-3.jpg';
import heroBGImage4 from '../../assets/hero-bg-4.jpg';
import heroBGImage5 from '../../assets/hero-bg-5.jpg';
import heroBGImage6 from '../../assets/hero-bg-6.jpg';
import heroBGImage7 from '../../assets/hero-bg-7.jpg';
import heroBGImage8 from '../../assets/hero-bg-8.jpg';
import heroBGImage9 from '../../assets/hero-bg-9.jpg';
import heroBGImage10 from '../../assets/hero-bg-10.jpg';

// Background images configuration (10 images total) - moved outside component for performance
const backgroundImages = [
    {
      id: 1,
      image: heroBGImage1,
      title: 'Find Your Dream Property',
      subtitle: 'Discover the best properties for sale across Pakistan'
    },
    {
      id: 2,
      image: heroBGImage2,
      title: 'Luxury Living Awaits',
      subtitle: 'Explore premium properties in prime locations'
    },
    {
      id: 3,
      image: heroBGImage3,
      title: 'Your Perfect Home is Here',
      subtitle: 'Browse thousands of verified property listings'
    },
    {
      id: 4,
      image: heroBGImage4,
      title: 'Invest in Your Future',
      subtitle: 'Find the perfect property for investment or living'
    },
    {
      id: 5,
      image: heroBGImage5,
      title: 'Premium Real Estate Opportunities',
      subtitle: 'Unlock exceptional properties across major cities'
    },
    {
      id: 6,
      image: heroBGImage6,
      title: 'Explore Affordable Housing Options',
      subtitle: 'Quality homes that fit your budget perfectly'
    },
    {
      id: 7,
      image: heroBGImage7,
      title: 'Invest in Pakistan\'s Top Cities',
      subtitle: 'Strategic locations for smart property investment'
    },
    {
      id: 8,
      image: heroBGImage8,
      title: 'Luxury Apartments & Modern Homes',
      subtitle: 'Experience contemporary living at its finest'
    },
    {
      id: 9,
      image: heroBGImage9,
      title: 'Your Trusted Property Guide',
      subtitle: 'Expert assistance for all your real estate needs'
    },
    {
      id: 10,
      image: heroBGImage10,
      title: 'Find Your Perfect Property',
      subtitle: 'Discover the Future of Real Estate'
    }
];

function Hero() {
  const [activeTab, setActiveTab] = useState('buy');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedPropertyType, setSelectedPropertyType] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [bedsCount, setBedsCount] = useState('');
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Tab-specific configuration
  const tabConfig = {
    buy: {
      propertyTypes: ['House', 'Flat', 'Plot', 'Commercial', 'Farmhouse', 'Penthouse'],
      priceRanges: ['Any', '0-50 Lac', '50 Lac-1 Crore', '1-2 Crore', '2-5 Crore', '5+ Crore'],
      showBeds: true
    },
    rent: {
      propertyTypes: ['House', 'Flat', 'Room', 'Portion', 'Commercial Space'],
      priceRanges: ['Any', '0-25K', '25-50K', '50K-1 Lac', '1-2 Lac', '2+ Lac'],
      showBeds: true
    },
    projects: {
      propertyTypes: ['Residential', 'Commercial', 'Mixed-Use', 'Gated Community'],
      priceRanges: ['Any', '50 Lac-1 Crore', '1-2 Crore', '2-5 Crore', '5+ Crore'],
      showBeds: false
    }
  };

  const cities = ['Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Multan', 'Peshawar', 'Quetta'];
  const bedsOptions = ['Any', '1', '2', '3', '4', '5+'];

  // Preload first image immediately for instant display
  useEffect(() => {
    const img = new Image();
    img.src = backgroundImages[0].image;
    // Preload for smooth initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-play background loop
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % 10); // 10 total images
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  // Reset filters when tab changes
  useEffect(() => {
    setSelectedCity('');
    setSelectedPropertyType('');
    setPriceRange('');
    setBedsCount('');
  }, [activeTab]);

  const handleSearch = () => {
    const searchParams = {
      tab: activeTab,
      city: selectedCity,
      propertyType: selectedPropertyType,
      priceRange: priceRange,
      beds: bedsCount
    };

    console.log('Search initiated:', searchParams);

    // Filter out empty values
    const activeFilters = Object.entries(searchParams)
      .filter(([key, value]) => value && value !== 'Any')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    // Simulate navigation to listings page with filters
    if (window.navigateTo) {
      sessionStorage.setItem('searchParams', JSON.stringify(activeFilters));
      window.navigateTo('listings');
    } else {
      alert(`Search initiated for ${activeTab}:\n${JSON.stringify(activeFilters, null, 2)}`);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    console.log(`Switched to ${tab} tab`);
  };

  const handlePrevBackground = () => {
    setIsAutoPlaying(false);
    setCurrentBgIndex((prev) => (prev - 1 + 10) % 10); // 10 total images
  };

  const handleNextBackground = () => {
    setIsAutoPlaying(false);
    setCurrentBgIndex((prev) => (prev + 1) % 10); // 10 total images
  };

  const config = tabConfig[activeTab];
  const currentBg = backgroundImages[currentBgIndex];

  return (
    <section className="hero">
      {/* Multiple background layers for smooth crossfade */}
      {backgroundImages.map((bg, index) => (
        <div
          key={bg.id}
          className={`hero-background ${index === currentBgIndex ? 'active' : ''}`}
          style={{ backgroundImage: `url(${bg.image})` }}
        />
      ))}

      <div className="hero-overlay"></div>

      {/* Background toggle buttons */}
      <button
        className="hero-bg-toggle-btn hero-bg-toggle-prev"
        onClick={handlePrevBackground}
        aria-label="Previous background"
      >
        <ChevronLeftIcon size={24} color="#ffffff" />
      </button>
      <button
        className="hero-bg-toggle-btn hero-bg-toggle-next"
        onClick={handleNextBackground}
        aria-label="Next background"
      >
        <ChevronRightIcon size={24} color="#ffffff" />
      </button>

      {/* Auto-play indicator dots */}
      <div className="hero-bg-indicators">
        {backgroundImages.map((bg, index) => (
          <button
            key={bg.id}
            className={`hero-bg-indicator ${index === currentBgIndex ? 'active' : ''}`}
            onClick={() => {
              setIsAutoPlaying(false);
              setCurrentBgIndex(index);
            }}
            aria-label={`Switch to background ${index + 1}`}
          />
        ))}
      </div>

      <div className="hero-content">
        <h1 className="hero-title">{currentBg.title}</h1>
        <p className="hero-subtitle">{currentBg.subtitle}</p>

        <div className="search-panel">
          <div className="search-tabs">
            <button
              className={`tab ${activeTab === 'buy' ? 'active' : ''}`}
              onClick={() => handleTabChange('buy')}
              aria-label="Search properties for sale"
            >
              Buy
            </button>
            <button
              className={`tab ${activeTab === 'rent' ? 'active' : ''}`}
              onClick={() => handleTabChange('rent')}
              aria-label="Search properties for rent"
            >
              Rent
            </button>
            <button
              className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
              onClick={() => handleTabChange('projects')}
              aria-label="Search new projects"
            >
              Projects
            </button>
          </div>

          <div className="search-form">
            <div className="form-group">
              <label htmlFor="city-select">City</label>
              <select
                id="city-select"
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="form-select"
                aria-label="Select city"
              >
                <option value="">All Cities</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="type-select">Property Type</label>
              <select
                id="type-select"
                value={selectedPropertyType}
                onChange={(e) => setSelectedPropertyType(e.target.value)}
                className="form-select"
                aria-label="Select property type"
              >
                <option value="">All Types</option>
                {config.propertyTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="price-select">
                {activeTab === 'rent' ? 'Monthly Rent' : 'Price Range'}
              </label>
              <select
                id="price-select"
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className="form-select"
                aria-label="Select price range"
              >
                <option value="">Any Price</option>
                {config.priceRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>

            {config.showBeds && (
              <div className="form-group">
                <label htmlFor="beds-select">Bedrooms</label>
                <select
                  id="beds-select"
                  value={bedsCount}
                  onChange={(e) => setBedsCount(e.target.value)}
                  className="form-select"
                  aria-label="Select number of bedrooms"
                >
                  {bedsOptions.map(beds => (
                    <option key={beds} value={beds}>{beds}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="hero-btn-search"
              onClick={handleSearch}
              aria-label={`Search ${activeTab} properties`}
            >
              <SearchIcon size={20} color="white" />
              <span>Search</span>
            </button>
          </div>

          {/* Active filters display */}
          {(selectedCity || selectedPropertyType || priceRange || bedsCount) && (
            <div className="active-filters">
              <span className="filters-label">Active filters:</span>
              {selectedCity && <span className="filter-tag">{selectedCity}</span>}
              {selectedPropertyType && <span className="filter-tag">{selectedPropertyType}</span>}
              {priceRange && <span className="filter-tag">{priceRange}</span>}
              {bedsCount && bedsCount !== 'Any' && <span className="filter-tag">{bedsCount} Beds</span>}
              <button
                className="clear-filters"
                onClick={() => {
                  setSelectedCity('');
                  setSelectedPropertyType('');
                  setPriceRange('');
                  setBedsCount('');
                }}
                aria-label="Clear all filters"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default Hero;
